/**
 * ThemeToggle — compact sun/moon button reflecting the resolved
 * theme, cycling to the OPPOSITE explicit choice on click.
 *
 * `aria-label` announces the destination state, not the current one,
 * to match every OS-level toggle convention (screen-reader users hear
 * "Switch to light theme" when we're in dark mode, etc.).
 *
 * `aria-pressed` reflects the DARK state so assistive tech reads it
 * as a two-state toggle without needing to know about the third
 * 'system' preference.
 */

import React, { useContext } from 'react';
import { ThemeContext } from './theme.jsx';

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}
function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

function ThemeToggle({ className = '' }) {
  // Read the context directly so we can render nothing when there's
  // no <ThemeProvider> in scope. Some rendering contexts (test
  // fixtures for pages that use <AuthLayout>, ancillary route
  // trees) don't wire theming — silently omitting the toggle is
  // friendlier than throwing.
  const ctx = useContext(ThemeContext);
  if (!ctx) return null;
  const { resolved, toggle } = ctx;
  const nextLabel = resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
  return (
    <button
      type="button"
      className={`theme-toggle ${className}`.trim()}
      onClick={toggle}
      aria-label={nextLabel}
      title={nextLabel}
      aria-pressed={resolved === 'dark'}
    >
      {resolved === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

export default ThemeToggle;
